import { NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { RootState } from '@next/core-lib/app';
import { Customer } from '@next/core-lib/business-partner-aggregation-api';
import {
  AbstractFormDialogData,
  AbstractFormDialog,
  AbstractDialogResultData,
  DialogService,
  DIALOG_RETURN_TYPES,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrDatePipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SelectedLocation } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { take, skipWhile } from 'rxjs/operators';
import { ViewMode } from 'src/app/shared/models/shared.model';

import { FecalScoring, FecesType, Pen } from '../../model/fecal-scoring.model';
import * as actions from '../../store/actions/fecal-scoring.actions';
import { getSelectedFecalScoring } from '../../store/selectors/fecal-scoring.selectors';

import { FecalScoringDetailPenFormComponent } from '../fecal-scoring-detail-pen-form/fecal-scoring-detail-pen-form.component';

export interface FecalScoringDetailDialogInputData extends AbstractFormDialogData {
  fecalScoringId?: number;
  locationId?: number;
  locations: SelectedLocation[];
  customer: Customer;
  viewMode: ViewMode;
}

export interface FecalScoringDetailDialogResultData {
  fecalScoring: FecalScoring;
}

@Component({
  selector: 'app-fecal-scoring-detail',
  templateUrl: './fecal-scoring-detail.component.html',
  styleUrls: ['./fecal-scoring-detail.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgIf,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    MatTabGroup,
    NgClass,
    MatTab,
    MatTabLabel,
    FecalScoringDetailPenFormComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class FecalScoringDetailComponent extends AbstractFormDialog<FecalScoringDetailDialogResultData> {
  @ViewChild('fecalScoringNameInput') fecalScoringNameInput: ElementRef;

  formGroup: FormGroup;
  penFormGroups: FormGroup[] = [];
  tabs: number[] = [];

  pageTitle: string = this.localeService.translate('fecal-scoring.detail.create.title');

  penTabsClass: string;
  currency: string;

  isMobile$ = this.breakpointService.ltSm$;

  fecalScoring: FecalScoring;
  location: SelectedLocation;

  selectedTabIndex = 0;
  pensDeleted = false;
  pensAdded = false;
  canDeletePen = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FecalScoringDetailDialogInputData,
    protected mdDialogRef: MatDialogRef<
      FecalScoringDetailComponent,
      AbstractDialogResultData<FecalScoringDetailDialogResultData>
    >,
    @Inject(BreakpointServiceInjector)
    private readonly breakpointService: BreakpointService,
    private readonly localeService: LocaleService,
    private readonly store: Store<RootState>,
    private readonly formBuilder: FormBuilder,
    private readonly dialog: DialogService,
  ) {
    super();

    this.isMobile$.subscribe((isMobile) => {
      this.penTabsClass = isMobile ? 'mobile' : '';
    });

    this.data.title = this.localeService.translate(`fecal-scoring.detail.create.title`);

    this.selectedTabIndex = 0;

    this.buildFormGroups(0);

    const id = this.data.fecalScoringId;
    if (id && id > 0) {
      this.store.dispatch(actions.setSelectedFecalScoringRequest({ id }));
      this.store
        .select(getSelectedFecalScoring)
        .pipe(
          skipWhile((c) => !c || c.id !== id),
          take(1),
        )
        .subscribe((fecalScoring) => {
          this.fecalScoring = fecalScoring;

          this.location = this.data.locations.find((l) => l.id === this.fecalScoring.locationId);

          this.pageTitle = `${this.localeService.translate('fecal-scoring.detail.edit.title')} ${
            fecalScoring.name
          }`;

          this.buildFormGroups(this.fecalScoring.pens.length);
          this.setFormValues();
        });
    } else {
      this.tabs = [0];
      this.buildFormGroups(this.tabs.length);

      this.location = this.data.locations.find((l) => l.id === this.data.locationId);
    }
  }

  private buildFormGroups(length: number): void {
    this.penFormGroups = [];

    this.buildPenFormGroup(length);

    this.formGroup = this.formBuilder.group({
      fecalScoringName: [null, [Validators.required]],
      pens: this.penFormGroups,
    });
  }

  private buildPenFormGroup(length: number) {
    for (let i = 0; i < length; i++) {
      const penScoreFormGroups: FormGroup[] = [];

      penScoreFormGroups.push(
        this.formBuilder.group({
          penScoreNormal: [null, [Validators.required]],
          penScoreSoft: [null, [Validators.required]],
          penScoreMild: [null, [Validators.required]],
          penScoreModerate: [null, [Validators.required]],
          penScoreSevere: [null, [Validators.required]],
          penScoreTotal: [null, [Validators.required, Validators.min(100), Validators.max(100)]],
        }),
      );

      this.penFormGroups.push(
        this.formBuilder.group({
          fecalScoringAge: [null, [Validators.required]],
          penScore: penScoreFormGroups,
        }),
      );
    }
  }

  private setFormValues() {
    this.tabs = [];

    this.formGroup.controls.fecalScoringName.setValue(this.fecalScoring.name);

    this.fecalScoring.pens.forEach((pen, index) => {
      this.tabs.push(index);
      if (pen.age > 0) {
        this.penFormGroups[index].controls.fecalScoringAge.setValue(pen.age);
      }

      const penScoreNormal = pen.penScores.find((p) => p.type === FecesType.NORMAL);

      const penscore = this.penFormGroups[index].controls.penScore as FormGroup;

      penscore.value.controls.penScoreNormal.setValue(
        penScoreNormal ? penScoreNormal.percentage : 0,
      );

      const penScoreSoft = pen.penScores.find((p) => p.type === FecesType.SOFT);

      penscore.value.controls.penScoreSoft.setValue(penScoreSoft ? penScoreSoft.percentage : 0);

      const penScoreMild = pen.penScores.find((p) => p.type === FecesType.MILD);

      penscore.value.controls.penScoreMild.setValue(penScoreMild ? penScoreMild.percentage : 0);

      const penScoreModerate = pen.penScores.find((p) => p.type === FecesType.MODERATE);
      penscore.value.controls.penScoreModerate.setValue(
        penScoreModerate ? penScoreModerate.percentage : 0,
      );

      const penScoreSevere = pen.penScores.find((p) => p.type === FecesType.SEVERE);
      penscore.value.controls.penScoreSevere.setValue(
        penScoreSevere ? penScoreSevere.percentage : 0,
      );
    });

    this.canDeletePen = this.tabs.length > 1;
  }

  getDate() {
    return this.fecalScoring?.date ?? new Date();
  }

  save() {
    if (this.validateForm()) {
      this.fecalScoring = {
        ...this.fecalScoring,
        locationId: this.fecalScoring?.locationId ?? this.data.locationId,
        name: <string>this.formGroup.controls.fecalScoringName.value,
        pens: this.getPensFromFormGroups(),
      };

      this.confirm({
        fecalScoring: this.fecalScoring,
      });
    }
  }

  //save and create new pen (NOT new fecalscore)
  alt() {
    if (this.validateForm()) {
      this.fecalScoring = {
        ...this.fecalScoring,
        locationId: this.fecalScoring?.locationId ?? this.data.locationId,
        name: <string>this.formGroup.controls.fecalScoringName.value,
        pens: this.getPensFromFormGroups(),
        createdBy: this.data.customer.name,
        createdAt: new Date().toISOString(),
        date: new Date().toISOString(),
      };

      this.store.dispatch(
        actions.upsertFecalScoringRequest({
          request: this.fecalScoring,
        }),
      );

      this.store
        .select(getSelectedFecalScoring)
        .pipe(
          skipWhile((c) => !c),
          take(1),
        )
        .subscribe((fecalScoring) => {
          this.fecalScoring = fecalScoring;
        });

      this.fecalScoring = {
        ...this.fecalScoring,
        pens: this.getPensFromFormGroups(),
      };

      this.store.dispatch(actions.clearSelectedFecalScoring());

      const newPens: Pen[] = [];

      this.fecalScoring.pens.forEach((pen) => {
        newPens.push(pen);
      });

      newPens.push({
        age: 0,
        penScores: [
          {
            type: FecesType.NORMAL,
            percentage: 0,
          },
          {
            type: FecesType.SOFT,
            percentage: 0,
          },
          {
            type: FecesType.MILD,
            percentage: 0,
          },
          {
            type: FecesType.MODERATE,
            percentage: 0,
          },
          {
            type: FecesType.SEVERE,
            percentage: 0,
          },
        ],
      });

      this.fecalScoring = {
        ...this.fecalScoring,
        pens: newPens,
      };

      this.buildFormGroups(newPens.length);
      this.setFormValues();
      this.selectedTabIndex = newPens.length;
      this.pensAdded = true;
    }
  }

  delete(penindex: number) {
    this.dialog
      .confirmWarning({
        title: this.localeService.translate('fecal-scoring.detail.delete-pen-confirmation.title'),
        text: this.localeService.translate('fecal-scoring.detail.delete-pen-confirmation.message'),
        pushHistoryState: false,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          const newPens: Pen[] = [];

          this.fecalScoring.pens.forEach((pen) => {
            newPens.push(pen);
          });

          newPens.splice(penindex, 1);

          this.fecalScoring = {
            ...this.fecalScoring,
            pens: newPens,
          };

          this.buildFormGroups(this.fecalScoring.pens.length);
          this.setFormValues();
          this.selectedTabIndex =
            this.fecalScoring.pens.length >= penindex ? penindex : penindex - 1;
          this.pensDeleted = true;
        }
      });
  }

  cancel() {
    if (
      !(
        this.formGroup.pristine &&
        this.penFormGroups.every((f) => f.pristine) &&
        this.penFormGroups.every(
          (f) => f.controls.penScore.value.pristine && !this.pensDeleted && !this.pensAdded,
        )
      )
    ) {
      this.dialog
        .pendingChanges({
          title: this.localeService.translate('fecal-scoring.detail.item-cancellation.title'),
          subTitle: this.localeService.translate('fecal-scoring.detail.item-cancellation.subtitle'),
          pushHistoryState: false,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.mdDialogRef.close();
          } else {
            this.canceling = false;
            this.mdDialogRef.disableClose = true;
          }
        });
    } else {
      this.mdDialogRef.close();
    }
  }

  private validateForm(): boolean {
    if (
      !(
        this.formGroup.valid &&
        this.penFormGroups.every((f) => f.valid) &&
        this.penFormGroups.every((pfg) => this.checkPenScoreTotal(pfg))
      )
    ) {
      this.focusOnInvalidTab();
      return false;
    }
    return true;
  }

  private focusOnInvalidTab() {
    const indexOfFailedTab = this.penFormGroups.findIndex((f: FormGroup) => !f.valid);
    if (indexOfFailedTab >= 0) {
      this.validateAllFormFields(this.penFormGroups[indexOfFailedTab]);
      this.selectedTabIndex = indexOfFailedTab;

      this.fecalScoringNameInput.nativeElement.focus();
    } else {
      const indexOfFailedTab = this.penFormGroups.findIndex(
        (f: FormGroup) => this.getPenScoreTotal(f) !== 100,
      );
      if (indexOfFailedTab >= 0) {
        this.validateAllFormFields(this.penFormGroups[indexOfFailedTab]);
        this.selectedTabIndex = indexOfFailedTab;

        this.fecalScoringNameInput.nativeElement.focus();
      }
    }
  }

  private validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  private getPensFromFormGroups(): Pen[] {
    const pens: Pen[] = [];

    this.penFormGroups.forEach((penGroup) => {
      pens.push({
        age: penGroup.controls.fecalScoringAge.value,
        penScores: [
          {
            type: FecesType.NORMAL,
            percentage: penGroup.value.penScore.value.penScoreNormal ?? 0,
          },
          {
            type: FecesType.SOFT,
            percentage: penGroup.value.penScore.value.penScoreSoft ?? 0,
          },
          {
            type: FecesType.MILD,
            percentage: penGroup.value.penScore.value.penScoreMild ?? 0,
          },
          {
            type: FecesType.MODERATE,
            percentage: penGroup.value.penScore.value.penScoreModerate ?? 0,
          },
          {
            type: FecesType.SEVERE,
            percentage: penGroup.value.penScore.value.penScoreSevere ?? 0,
          },
        ],
      });
    });

    return pens;
  }

  private checkPenScoreTotal(penFormGroup: FormGroup): boolean {
    const penScoreNormal = penFormGroup.value.penScore.value.penScoreNormal ?? 0;
    const penScoreSoft = penFormGroup.value.penScore.value.penScoreSoft ?? 0;
    const penScoreMild = penFormGroup.value.penScore.value.penScoreMild ?? 0;
    const penScoreModerate = penFormGroup.value.penScore.value.penScoreModerate ?? 0;
    const penScoreSevere = penFormGroup.value.penScore.value.penScoreSevere ?? 0;

    const total = penScoreNormal + penScoreSoft + penScoreMild + penScoreModerate + penScoreSevere;

    const penscore = penFormGroup.controls.penScore.value as FormGroup;

    penscore.controls.penScoreTotal.setValue(total);
    penscore.controls.penScoreTotal.markAsDirty();

    if (total === 100) {
      return true;
    }
    return false;
  }

  private getPenScoreTotal(penFormGroup: FormGroup): number {
    const penscore = penFormGroup.controls.penScore.value as FormGroup;

    return penscore.value.penScoreTotal;
  }

  keyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && this.validateForm()) {
      this.confirm();
      event.preventDefault();
    }
  }
}
